import React, { useState, useRef, useEffect } from 'react';
import { TextInput } from "../../../../components/controls/inputs";
import Styles from './Styles/MarkConfirmationModal.module.scss';

export enum MarkConfirmationType {
    Kiz = 'КИЗ',
    Box = 'SSCC',
    Palette = 'SSCC паллеты',
    KizOsu = 'ГТИН ОСУ'
}

interface MarkConfirmationProps {
    headerText: string,
    scanText: string,
    onClose: () => void;
    onConfirm: (sscc: string) => void;
    validate: (sscc: string) => Promise<boolean>;
    markType: MarkConfirmationType
}

const MarkConfirmationModal: React.FC<MarkConfirmationProps> = ({
    headerText,
    scanText,
    onClose,
    onConfirm,
    validate,
    markType
}) => {
    const [ssccValue, setSsccValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, []);

    const handleInputChange = (value: string) => {
        setSsccValue(value);
        setError(null);
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter') return;

        await handleConfirm();
    };

    const handleConfirm = async () => {
        if (!ssccValue.trim()) {
            setError(`Введите ${markType}`);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await validate(ssccValue.trim());
            if (result) {
                onConfirm(ssccValue.trim());
            } else {
                setError(`Некорректный ${markType}`);
            }
        } catch (err) {
            setError(`Ошибка проверки ${markType}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setSsccValue('');
        setError(null);
        onClose();
    };

    return (
        <div className={Styles.modalOverlay}>
            <div className={Styles.modal}>
                <div className={Styles.modalHeader}>
                    <h3>{headerText ?? ""}</h3>
                    <button
                        className={Styles.closeButton}
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>

                <div className={Styles.modalBody}>
                    <TextInput
                        ref={inputRef}
                        className={Styles.TextInput}
                        autoFocus
                        labelClassName={Styles.inlineLabel}
                        inputClassName={Styles.inlineInput}
                        value={ssccValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={scanText}
                    />

                    {error && (
                        <div className={Styles.error}>
                            {error}
                        </div>
                    )}
                </div>

                <div className={Styles.modalFooter}>
                    <button
                        className={Styles.cancelButton}
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Отмена
                    </button>
                    <button
                        className={Styles.confirmButton}
                        onClick={handleConfirm}
                        disabled={isLoading || !ssccValue.trim()}
                    >
                        {isLoading ? 'Проверка...' : 'Подтвердить'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MarkConfirmationModal;
